// Session Explorer - lookup and display session timeline

import { api } from './api';
import { formatDateTime, formatDuration, formatPrice, $, show, hide, setHTML } from './utils';
import type { SessionTimeline } from './types';

export async function loadSessionExplorer(): Promise<void> {
  const input = document.getElementById('explorerSessionId') as HTMLInputElement;
  const sessionId = input?.value.trim();
  
  if (!sessionId) {
    alert('Please enter a session ID');
    return;
  }
  
  // Show loading state
  hide('explorerEmpty');
  hide('explorerNotFound');
  hide('explorerResults');
  show('explorerLoading');
  
  try {
    // Load timeline which includes ALL events (chatbot + e-commerce)
    const timeline = await api.getSessionTimelineById(sessionId);
    
    // Hide loading, show results
    hide('explorerLoading');
    show('explorerResults');
    
    // Render all sections
    renderExplorerSummary(timeline);
    renderExplorerChatbot(timeline);
    renderExplorerRecommendations(timeline.recommendations || []);
    renderExplorerOrder(timeline.order);
    // Use timeline events which include ALL event types
    renderExplorerTimeline(timeline.timeline || []);
    
  } catch (error: any) {
    console.error('Error loading session:', error);
    hide('explorerLoading');
    show('explorerNotFound');
  }
}

function renderExplorerSummary(timeline: SessionTimeline): void {
  const container = $('explorerSummaryContent');
  if (!container) return;
  
  const durationFormatted = formatDuration(timeline.durationSeconds || 0);
  
  container.innerHTML = `
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
      <div style="background: white; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Session ID</div>
        <div style="font-family: monospace; font-size: 12px; word-break: break-all;">${timeline.sessionId}</div>
      </div>
      <div style="background: white; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Client</div>
        <div style="font-weight: 600;">${timeline.clientId || 'Unknown'}</div>
      </div>
      <div style="background: white; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Started</div>
        <div style="font-weight: 600;">${timeline.startedAt ? formatDateTime(timeline.startedAt) : 'Unknown'}</div>
      </div>
      <div style="background: white; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Duration</div>
        <div style="font-weight: 600;">${durationFormatted}</div>
      </div>
      <div style="background: white; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Chatbot Assisted</div>
        <div style="font-weight: 600; color: ${timeline.chatbotAssisted ? '#27ae60' : '#95a5a6'};">
          ${timeline.chatbotAssisted ? '✅ Yes' : '❌ No'}
        </div>
      </div>
      <div style="background: white; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Converted</div>
        <div style="font-weight: 600; color: ${timeline.converted ? '#27ae60' : '#95a5a6'};">
          ${timeline.converted ? '✅ Yes' : '❌ No'}
        </div>
      </div>
    </div>
    ${timeline.stats ? `
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-top: 15px;">
      <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; text-align: center;">
        <div style="font-size: 20px; font-weight: 600; color: #3498db;">${timeline.stats.pageViews || 0}</div>
        <div style="font-size: 11px; color: #666;">Page Views</div>
      </div>
      <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; text-align: center;">
        <div style="font-size: 20px; font-weight: 600; color: #9b59b6;">${timeline.stats.productsViewed || 0}</div>
        <div style="font-size: 11px; color: #666;">Products Viewed</div>
      </div>
      <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; text-align: center;">
        <div style="font-size: 20px; font-weight: 600; color: #e67e22;">${timeline.stats.productsAddedToCart || 0}</div>
        <div style="font-size: 11px; color: #666;">Added to Cart</div>
      </div>
      <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; text-align: center;">
        <div style="font-size: 20px; font-weight: 600; color: #27ae60;">${timeline.stats.recommendationsShown || 0}</div>
        <div style="font-size: 11px; color: #666;">Recommendations</div>
      </div>
    </div>
    ` : ''}
  `;
}

function renderExplorerChatbot(timeline: SessionTimeline): void {
  const container = $('explorerChatbotContent');
  if (!container) return;
  
  if (!timeline.chatbotAssisted) {
    container.innerHTML = `
      <div style="color: #95a5a6; text-align: center; padding: 20px;">
        No chatbot interaction in this session
      </div>
    `;
    return;
  }
  
  const chatDurationFormatted = formatDuration(timeline.chatbotDuration || 0);
  
  container.innerHTML = `
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px;">
      <div style="background: #f0f9ff; padding: 15px; border-radius: 8px; text-align: center;">
        <div style="font-size: 24px; font-weight: 600; color: #3498db;">${timeline.messageCount || 0}</div>
        <div style="font-size: 12px; color: #666;">Messages</div>
      </div>
      <div style="background: #f0fff4; padding: 15px; border-radius: 8px; text-align: center;">
        <div style="font-size: 24px; font-weight: 600; color: #27ae60;">${timeline.stats?.recommendationsShown || 0}</div>
        <div style="font-size: 12px; color: #666;">Recommendations</div>
      </div>
      <div style="background: #fef9e7; padding: 15px; border-radius: 8px; text-align: center;">
        <div style="font-size: 24px; font-weight: 600; color: #f39c12;">${chatDurationFormatted}</div>
        <div style="font-size: 12px; color: #666;">Chat Duration</div>
      </div>
    </div>
  `;
}

function renderExplorerRecommendations(recommendations: SessionTimeline['recommendations']): void {
  const container = $('explorerRecommendationsContent');
  if (!container) return;
  
  if (!recommendations || recommendations.length === 0) {
    container.innerHTML = `
      <div style="color: #95a5a6; text-align: center; padding: 20px;">
        No product recommendations in this session
      </div>
    `;
    return;
  }
  
  container.innerHTML = `
    <table style="width: 100%;">
      <thead>
        <tr>
          <th>Product</th>
          <th>Price</th>
          <th>Recommended At</th>
          <th>Clicked</th>
          <th>Added to Cart</th>
          <th>Purchased</th>
        </tr>
      </thead>
      <tbody>
        ${recommendations.map(rec => `
          <tr>
            <td>
              <div style="font-weight: 600;">${rec.productName || rec.productId}</div>
              ${rec.productSku ? `<div style="font-size: 11px; color: #999;">SKU: ${rec.productSku}</div>` : ''}
            </td>
            <td>${rec.productPrice ? formatPrice(rec.productPrice) : '<span style="color: #999;">-</span>'}</td>
            <td>${formatDateTime(rec.recommendedAt)}</td>
            <td style="text-align: center;">${rec.wasClicked ? '✅' : '❌'}</td>
            <td style="text-align: center;">${rec.wasAddedToCart ? '✅' : '❌'}</td>
            <td style="text-align: center;">${rec.wasPurchased ? '✅' : '❌'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

function renderExplorerOrder(order: SessionTimeline['order']): void {
  const container = $('explorerOrder');
  const content = $('explorerOrderContent');
  
  if (!container || !content) return;
  
  if (!order) {
    container.style.display = 'none';
    return;
  }
  
  container.style.display = 'block';
  content.innerHTML = `
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px;">
      <div style="background: #f0fff4; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Order ID</div>
        <div style="font-weight: 600;">#${order.orderId}</div>
      </div>
      <div style="background: #f0fff4; padding: 15px; border-radius: 8px;">
        <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Total</div>
        <div style="font-weight: 600; font-size: 20px; color: #27ae60;">${formatPrice(order.total || 0)}</div>
      </div>
    </div>
    ${order.items && order.items.length > 0 ? `
    <h4 style="margin-bottom: 10px;">Order Items</h4>
    <table style="width: 100%;">
      <thead>
        <tr>
          <th>Product</th>
          <th>Qty</th>
          <th>Price</th>
          <th>Recommended</th>
        </tr>
      </thead>
      <tbody>
        ${order.items.map(item => `
          <tr>
            <td>${item.productName}</td>
            <td>${item.quantity}</td>
            <td>${formatPrice(item.price || 0)}</td>
            <td style="text-align: center;">${item.wasRecommended ? '🎁 Yes' : '-'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    ` : ''}
    ${order.coupons && order.coupons.length > 0 ? `
    <div style="margin-top: 15px;">
      <strong>Coupons:</strong> ${order.coupons.map(c => `<code>${c.code}</code> (-${formatPrice(c.discount || 0)})`).join(', ')}
    </div>
    ` : ''}
  `;
}

function renderExplorerTimeline(events: any[]): void {
  const container = $('explorerTimeline');
  if (!container) return;
  
  if (!events || events.length === 0) {
    container.innerHTML = `
      <div style="color: #95a5a6; text-align: center; padding: 40px;">
        No events found for this session
      </div>
    `;
    return;
  }
  
  // Timeline events are structured objects with title, description, icon, etc.
  container.innerHTML = `
    <div style="max-height: 500px; overflow-y: auto;">
      ${events.map((event: any) => {
        const icon = event.icon || '📌';
        const label = event.title || event.eventType;
        const content = event.description || '';
        
        // Determine background color
        let bgColor = 'white';
        if (event.eventType === 'message_received' || event.category === 'chatbot') {
          bgColor = '#f8f9fa';
        }
        
        return `
          <div style="padding: 12px; border-bottom: 1px solid #eee; background: ${bgColor};">
            <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
              <strong><span style="font-size: 18px;">${icon}</span> ${label}</strong>
              <span style="color: #999; font-size: 12px;">${formatDateTime(event.timestamp)}</span>
            </div>
            ${content ? `<p style="color: #333; margin-top: 8px; white-space: pre-wrap; line-height: 1.5;">${content}</p>` : ''}
            ${event.productName ? `<div style="margin-top: 5px;"><span style="background: #e8f5e9; color: #388e3c; padding: 2px 8px; border-radius: 4px; font-size: 12px;">🏷️ ${event.productName}</span></div>` : ''}
          </div>
        `;
      }).join('')}
    </div>
  `;
}

// Initialize session explorer event listeners
export function initSessionExplorer(): void {
  const input = document.getElementById('explorerSessionId');
  if (input) {
    input.addEventListener('keypress', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') {
        loadSessionExplorer();
      }
    });
  }
}

// Export for global access
(window as any).loadSessionExplorer = loadSessionExplorer;
